import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetMessageQuery as GetMessage } from '__generated__/graphql';

import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import EditMessageMutation from './EditMessageMutation';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useValidation from '../../../common/useValidation';

type Message = GetMessage['project']['message'];

const messageFormSchema = yup.object().shape({
  title: yup.string().min(1).max(100).required(),
});

export interface EditMessageTitleFormProps {
  message: Message;
  onSuccess?: () => void;
}

const EditMessageTitleForm = ({ message, onSuccess }: EditMessageTitleFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();

  const getValues = (message: Partial<Message>) => ({
    id: message.id,
    title: message.title,
  });

  const form = useForm({
    values: getValues(message),
  });

  useNavigationPrompt(form.changed());

  const [editMessage, { error, loading }] = useMutation(EditMessageMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ editMessage: message }) => {
      onSuccess?.();

      notifier.success(t('notifications.message_saved'));

      form.reset(getValues(message));
    },
    onError: () => {
      notifier.error(t('something_went_wrong'));
    },
  });

  const { valid, errors } = useValidation({
    schema: messageFormSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editMessage()}>
      <UI.FormGrid>
        <UI.H4>
          {t('edit_message')}
        </UI.H4>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
          <UI.InputLabel htmlFor="MessageTitle">
            {t('title')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.InputDescription>
            {t('message_title_description')}
          </UI.InputDescription>
          <UI.Input {...form.text('title')} id="MessageTitle" />
          <UI.ErrorMessages attribute={t('title')} errors={errors.title} />
        </UI.InputGroup>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_message')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditMessageTitleForm;
